import React, { useState, useEffect } from 'react';
import ApiService from '../services/ApiService';
import Avatar from './Avatar';
import './UserProfileModal.css';

interface User {
  id: number;
  name?: string;
  email: string;
  distance?: number | null;
  profile?: {
    bio?: string;
    age?: number;
    location?: string;
    interests?: string;
    photoUrl?: string;
    photos?: Array<{ id: number; url: string; order: number }>;
    variant?: {
      name: string;
      description?: string;
    };
  };
  mood?: {
    mood: string;
    expiresAt: string;
  } | null;
  greenFlags?: Array<{
    flag: {
      id: number;
      name: string;
      emoji?: string;
      description: string;
      category: string;
    };
    verifiedBy?: number;
  }>;
}

interface Props {
  user: User;
  onClose: () => void;
  onLike: () => void;
  onPass: () => void;
  actionLoading: boolean;
}

const MOOD_LABELS: Record<string, string> = {
  deep_conversation: 'Deep Conversation 💭',
  laugh: 'Want to Laugh 😄',
  planning: 'Planning Mode 📅',
  flirty: 'Flirty 😘',
  casual: 'Casual Chat 💬',
  adventurous: 'Adventurous 🗺️'
};

const UserProfileModal: React.FC<Props> = ({ user, onClose, onLike, onPass, actionLoading }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [fullProfile, setFullProfile] = useState<User | null>(null);

  useEffect(() => {
    // Fetch full profile details including green flags
    fetchFullProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const fetchFullProfile = async () => {
    try {
      const response = await ApiService.get(`/users/${user.id}`);
      setFullProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch full profile:', error);
      setFullProfile(user); // Fallback to provided user data
    }
  };

  const displayUser = fullProfile || user;
  const allPhotos = [
    displayUser.profile?.photoUrl,
    ...(displayUser.profile?.photos?.sort((a, b) => a.order - b.order).map(p => p.url) || [])
  ].filter(Boolean) as string[];

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };

  const groupedFlags = displayUser.greenFlags?.reduce((acc: any, userFlag) => {
    const category = userFlag.flag.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(userFlag);
    return acc;
  }, {});

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        {/* Photo Gallery */}
        <div className="profile-gallery">
          {allPhotos.length > 0 ? (
            <>
              <img
                src={allPhotos[currentPhotoIndex]}
                alt={displayUser.name || 'Profile'}
                className="gallery-photo"
              />
              {allPhotos.length > 1 && (
                <>
                  <button className="gallery-nav gallery-prev" onClick={prevPhoto}>‹</button>
                  <button className="gallery-nav gallery-next" onClick={nextPhoto}>›</button>
                  <div className="gallery-dots">
                    {allPhotos.map((_, idx) => (
                      <span
                        key={idx}
                        className={`dot ${idx === currentPhotoIndex ? 'active' : ''}`}
                        onClick={() => setCurrentPhotoIndex(idx)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="no-photo-placeholder">
              <Avatar photoUrl={null} name={displayUser.name} size="large" />
            </div>
          )}
        </div>

        {/* Profile Content */}
        <div className="profile-content">
          <div className="profile-header">
            <h2>
              {displayUser.name || 'Anonymous'}
              {displayUser.profile?.age && <span className="age-badge">, {displayUser.profile.age}</span>}
            </h2>
          </div>

          {displayUser.profile?.variant && (
            <div className="personality-badge">
              {displayUser.profile.variant.name}
            </div>
          )}

          {displayUser.mood && new Date(displayUser.mood.expiresAt) > new Date() && (
            <div className="mood-badge">
              {MOOD_LABELS[displayUser.mood.mood] || displayUser.mood.mood}
            </div>
          )}

          {displayUser.profile?.location && (
            <p className="location-text">
              📍 {displayUser.profile.location}
              {displayUser.distance && ` • ${displayUser.distance} miles away`}
            </p>
          )}

          {displayUser.profile?.bio && (
            <div className="profile-section">
              <h3>About</h3>
              <p>{displayUser.profile.bio}</p>
            </div>
          )}

          {displayUser.profile?.interests && (
            <div className="profile-section">
              <h3>Interests</h3>
              <p>{displayUser.profile.interests}</p>
            </div>
          )}

          {displayUser.greenFlags && displayUser.greenFlags.length > 0 && (
            <div className="profile-section">
              <h3>Green Flags</h3>
              {Object.entries(groupedFlags || {}).map(([category, flags]: [string, any]) => (
                <div key={category} className="flag-category">
                  <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                  <div className="flags-grid">
                    {flags.map((userFlag: any) => (
                      <div key={userFlag.flag.id} className="flag-chip">
                        {userFlag.flag.emoji} {userFlag.flag.name}
                        {userFlag.verifiedBy && <span className="verified-badge">✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ textAlign: 'center', padding: '10px', background: 'yellow', fontWeight: 'bold', fontSize: '20px' }}>
            VERSION 99.99 - TEST BUILD
          </div>
          <div className="profile-actions" style={{ position: 'relative', zIndex: 999 }}>
            <button
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                alert('❌ GREY PASS BUTTON TOUCHED!');
                console.log('Pass button TOUCH START');
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                alert('❌ GREY PASS BUTTON CLICKED!');
                console.log('Pass button clicked!', e);
                if (actionLoading) {
                  return;
                }
                onPass();
              }}
              className="action-btn pass-btn"
              style={{
                pointerEvents: 'auto',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                zIndex: 9999,
                position: 'relative',
                background: 'linear-gradient(135deg, #e0e0e0 0%, #c0c0c0 100%)'
              }}
            >
              ✕ PASS (GREY)
            </button>
            <button
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                alert('❤️ PINK LIKE BUTTON TOUCHED!');
                console.log('Like button TOUCH START');
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                alert('❤️ PINK LIKE BUTTON CLICKED!');
                console.log('Like button clicked!', e);
                if (actionLoading) {
                  return;
                }
                onLike();
              }}
              className="action-btn like-btn"
              style={{
                pointerEvents: 'auto',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                zIndex: 9999,
                position: 'relative',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white'
              }}
            >
              ♥ LIKE (PINK)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
